import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { InvoiceMatchPolicyDto } from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { useAuth } from "@/contexts/auth-context";

// US-037 — invoice match policy. Two render paths on one route:
//   • Admin → editable form (price tolerance %, add-on flat $, add-on %).
//   • Finance / ProcurementOfficer → read-only summary panel.
//   • Requester → access-denied panel (the API enforces 403 regardless).

export const Route = createFileRoute("/settings/match-policy")({
  head: () => ({
    meta: [
      { title: "Invoice Match Policy — ProcureFlow" },
      {
        name: "description",
        content: "Configure the price-tolerance and add-on thresholds used by matching.",
      },
    ],
  }),
  component: MatchPolicyPage,
});

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; data: InvoiceMatchPolicyDto }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

function bpsToPct(bps: number): string {
  // Strip trailing zeros so 500 → "5", 50 → "0.5", 250 → "2.5".
  const pct = bps / 100;
  return Number.isInteger(pct) ? String(pct) : String(pct);
}
function pctToBps(input: string): number {
  const n = Number(input);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}
function centsToDollars(cents: number): string {
  return String(Math.round(cents / 100));
}
function dollarsToCents(input: string): number {
  const n = Number(input);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

function MatchPolicyPage() {
  const { user } = useAuth();
  const [state, setState] = useState<FetchState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth("/api/invoices/match-policy");
        if (cancelled) return;
        if (res.status === 403) {
          setState({ kind: "forbidden" });
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setState({ kind: "error", message: body.message ?? "Failed to load policy." });
          return;
        }
        const { data } = (await res.json()) as { data: InvoiceMatchPolicyDto };
        if (!cancelled) setState({ kind: "loaded", data });
      } catch (err) {
        if (!cancelled)
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Unexpected error.",
          });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="space-y-6" data-testid="match-policy-section">
        <PageHeader title="Invoice Match Policy" description="Loading…" />
      </div>
    );
  }
  if (state.kind === "forbidden") {
    return (
      <div className="space-y-6" data-testid="match-policy-section">
        <PageHeader
          title="Invoice Match Policy"
          description="You don't have access to invoice matching settings."
        />
        <div
          className="rounded-2xl bg-card border border-border p-10 text-sm text-muted-foreground shadow-elegant"
          data-testid="match-policy-forbidden"
        >
          This page is restricted to Admin, Finance, and Procurement officers.
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="space-y-6" data-testid="match-policy-section">
        <PageHeader title="Invoice Match Policy" description="Something went wrong." />
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {state.message}
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === "Admin";
  return (
    <div className="space-y-6" data-testid="match-policy-section">
      <PageHeader
        title="Invoice Match Policy"
        description={
          isAdmin
            ? "Configure the thresholds that govern three-way matching."
            : "Current thresholds governing three-way matching."
        }
      />
      {isAdmin ? (
        <MatchPolicyForm
          initial={state.data}
          onSaved={(data) => setState({ kind: "loaded", data })}
        />
      ) : (
        <MatchPolicyReadOnly policy={state.data} />
      )}
    </div>
  );
}

function MatchPolicyForm({
  initial,
  onSaved,
}: {
  initial: InvoiceMatchPolicyDto;
  onSaved: (next: InvoiceMatchPolicyDto) => void;
}) {
  const [priceTol, setPriceTol] = useState(bpsToPct(initial.price_tolerance_bps));
  const [addonFlat, setAddonFlat] = useState(centsToDollars(initial.addon_flat_threshold_cents));
  const [addonPct, setAddonPct] = useState(bpsToPct(initial.addon_percent_threshold_bps));
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        price_tolerance_bps: pctToBps(priceTol),
        addon_flat_threshold_cents: dollarsToCents(addonFlat),
        addon_percent_threshold_bps: pctToBps(addonPct),
      };
      if (
        !Number.isInteger(body.price_tolerance_bps) ||
        !Number.isInteger(body.addon_flat_threshold_cents) ||
        !Number.isInteger(body.addon_percent_threshold_bps)
      ) {
        setError("All fields must be valid numbers.");
        return;
      }
      const res = await fetchWithAuth("/api/invoices/match-policy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const respBody = (await res.json().catch(() => ({}))) as {
        data?: InvoiceMatchPolicyDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(respBody.message ?? "Could not save policy.");
        return;
      }
      if (respBody.data) {
        onSaved(respBody.data);
        setToast("Matching policy updated.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      data-testid="match-policy-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitting) void handleSubmit();
      }}
      className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-5"
    >
      {toast && (
        <div className="rounded-lg border border-border bg-success/10 px-3 py-2 text-xs [color:var(--success)]">
          {toast}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
        <FormField
          id="price-tolerance"
          label="Price tolerance (%)"
          hint="Per-line price variance allowed before a line fails matching."
          input={
            <input
              id="price-tolerance"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={priceTol}
              onChange={(e) => setPriceTol(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          }
        />
        <FormField
          id="addon-flat"
          label="Add-on flat threshold"
          hint="Total add-on amount below this auto-approves; above triggers review."
          input={
            <input
              id="addon-flat"
              type="number"
              min={0}
              step="1"
              value={addonFlat}
              onChange={(e) => setAddonFlat(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          }
        />
        <FormField
          id="addon-percent"
          label="Add-on percent threshold (%)"
          hint="Add-ons under this % of invoice subtotal auto-approve."
          input={
            <input
              id="addon-percent"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={addonPct}
              onChange={(e) => setAddonPct(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          }
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save Policy"}
        </button>
      </div>
    </form>
  );
}

function MatchPolicyReadOnly({ policy }: { policy: InvoiceMatchPolicyDto }) {
  return (
    <div
      data-testid="match-policy-readonly"
      className="rounded-2xl bg-card border border-border shadow-elegant p-6"
    >
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
        <Stat label="Price tolerance" value={`${bpsToPct(policy.price_tolerance_bps)}%`} />
        <Stat
          label="Add-on flat threshold"
          value={`${centsToDollars(policy.addon_flat_threshold_cents)}`}
        />
        <Stat
          label="Add-on percent threshold"
          value={`${bpsToPct(policy.addon_percent_threshold_bps)}%`}
        />
      </dl>
    </div>
  );
}

function FormField({
  id,
  label,
  hint,
  input,
}: {
  id: string;
  label: string;
  hint?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <div className="mt-1">{input}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
